import { applicationIdentityKey, isAggregateCouncilScope } from '../councils';
import { confirmsScottishReadModelScope, SCOTTISH_READ_MODEL_COUNTRY } from '../planning/scottishReadModelScope';
import { parseRetryAfterMs, rateLimitRetryAt } from '../httpRetryAfter';
import { isLondonScope, LONDON_API_ORIGIN, LondonFeedError, readLondonApplication, readLondonFeed, readLondonMap } from './londonPlanningService';
import { usesSharedLondonDelivery } from '../planning/londonSharedDelivery';
import { readSharedLondonPayload, SharedLondonError } from './sharedLondonPlanningService';

export type ApplicationsApiErrorKind = 'timeout' | 'network' | 'http' | 'validation' | 'cancelled';

export class ApplicationsApiError extends Error {
  kind: ApplicationsApiErrorKind;
  status?: number;
  url: string;
  cause?: unknown;
  retryAfterMs?: number;

  constructor(message: string, kind: ApplicationsApiErrorKind, url: string, status?: number, cause?: unknown) {
    super(message);
    this.name = 'ApplicationsApiError';
    this.kind = kind;
    this.url = url;
    this.status = status;
    this.cause = cause;
  }
}

type FetchLike = typeof fetch;
type FeedSource = 'planning' | 'short_term_let';
type MapFeedScope = 'newest' | 'viewport';
type FetchBatchInfo = {
  accumulatedCount: number;
  source?: FeedSource;
};
type FetchBatchHandler = (batch: Record<string, unknown>[], info: FetchBatchInfo) => void | Promise<void>;

export type PlanningFeedBounds = {
  min_lng: number;
  min_lat: number;
  max_lng: number;
  max_lat: number;
};

export type PlanningFeedFacetItem = { value: string; count: number };
export type PlanningFeedFacets = {
  councils?: PlanningFeedFacetItem[];
  categories?: PlanningFeedFacetItem[];
  statuses?: PlanningFeedFacetItem[];
  tags?: PlanningFeedFacetItem[];
  postcodePrefixes?: PlanningFeedFacetItem[];
  documents?: { withDocuments: number; withoutDocuments: number };
  forms?: { withForms: number; withoutForms: number };
};

export type PlanningFeedLightPage = {
  applications: Record<string, unknown>[];
  count: number;
  total: number;
  next_cursor: string | null;
  facets: PlanningFeedFacets;
  fetched_at?: string;
  data_version?: number | string | null;
  partial?: boolean;
  date_basis?: 'validated';
  window_start?: string;
  window_end_exclusive?: string;
};

type FetchApplicationsOptions = {
  limit?: number;
  offset?: number;
  query?: string;
  council?: string;
  community?: string;
  areaId?: string;
  signal?: AbortSignal;
  retries?: number;
  timeoutMs?: number;
  fetchImpl?: FetchLike;
  headers?: Record<string, string>;
};

export type FetchPlanningFeedLightPageOptions = FetchApplicationsOptions & {
  includeShortLets?: boolean;
  cursor?: string | null;
  categories?: string[];
  excludeCategories?: string[];
  tags?: string[];
  statuses?: string[];
  decisionStatus?: 'pending' | 'decided' | 'refused' | 'withdrawn' | null;
  postcodePrefixes?: string[];
  hasDocuments?: boolean | null;
  hasForms?: boolean | null;
  impactMin?: number | null;
  impactMax?: number | null;
  activityFrom?: string | null;
  activityTo?: string | null;
  addedFrom?: string | null;
  addedTo?: string | null;
  interesting?: boolean | null;
  bounds?: PlanningFeedBounds | null;
  centerLat?: number | null;
  centerLng?: number | null;
  radiusKm?: number | null;
  includeFacets?: boolean;
  includeTotal?: boolean;
  validatedFrom?: string;
  validatedEndExclusive?: string;
  boundsEndExclusive?: boolean;
};

async function londonOperation<T>(operation: () => Promise<T>): Promise<T> {
  try { return await operation(); }
  catch (error) {
    if (error instanceof LondonFeedError) {
      throw new ApplicationsApiError(error.message, error.status === 400 ? 'validation' : 'http', LONDON_API_ORIGIN, error.status, error);
    }
    throw error;
  }
}

function rejectUnsupportedLondonFilters(options: FetchPlanningFeedLightPageOptions) {
  const arrayFilters = [options.categories, options.excludeCategories, options.tags, options.statuses, options.postcodePrefixes];
  const scalarFilters = [options.community, options.areaId, options.decisionStatus, options.hasDocuments, options.hasForms,
    options.impactMin, options.impactMax, options.activityFrom, options.activityTo, options.addedFrom, options.addedTo,
    options.interesting, options.centerLat, options.centerLng, options.radiusKm];
  if (arrayFilters.some(value => value?.length) || scalarFilters.some(value => value !== undefined && value !== null && value !== '') ||
      options.includeShortLets || options.includeFacets || (options.offset !== undefined && options.offset !== 0)) {
    throw new ApplicationsApiError('This London source filter is not available', 'validation', LONDON_API_ORIGIN, 400);
  }
}

function sharedLondonFilters(options: FetchPlanningFeedLightPageOptions) {
  return {
    community:options.community,area_id:options.areaId,category_value:options.categories,
    exclude_category_value:options.excludeCategories,tags:options.tags?.join(','),status:options.statuses?.join(','),
    decision_status:options.decisionStatus,postcode_prefixes:options.postcodePrefixes?.join(','),
    has_documents:options.hasDocuments,has_forms:options.hasForms,impact_min:options.impactMin,impact_max:options.impactMax,
    activity_from:options.activityFrom,activity_to:options.activityTo,added_from:options.addedFrom,added_to:options.addedTo,
    interesting:options.interesting,center_lat:options.centerLat,center_lng:options.centerLng,radius_km:options.radiusKm,
  };
}

function londonDetailUid(id: string | number, options: FetchApplicationByIdOptions): string | null {
  const value = String(id);
  const prefix = value.includes(':') ? value.split(':')[0] : undefined;
  if (!isLondonScope(options.council) && !isLondonScope(prefix)) return null;
  if ((prefix && options.council && options.council !== 'london' && options.council !== prefix) ||
      options.areaId || (!prefix && (!options.council || options.council === 'london'))) {
    throw new ApplicationsApiError('London detail requires an exact council identity', 'validation', LONDON_API_ORIGIN, 400);
  }
  return prefix ? value : options.council + ':' + value.replace(/\s/g, '').toUpperCase();
}

async function londonDetail(uid: string, options: FetchApplicationByIdOptions) {
  try { return await londonOperation(() => readLondonApplication(uid, options)); }
  catch (error) {
    if (error instanceof ApplicationsApiError && error.status === 404) return null;
    throw error;
  }
}

type FetchApplicationsLightOptions = FetchApplicationsOptions;
type FetchShortTermLetsOptions = FetchApplicationsOptions;
type FetchShortTermLetsLightOptions = FetchApplicationsOptions;
type FetchUnifiedPlanningFeedOptions = FetchApplicationsOptions & {
  includeShortLets?: boolean;
};
type FetchAllOptions = Omit<FetchApplicationsOptions, 'limit' | 'offset'> & {
  pageSize?: number;
  maxPages?: number;
  pageConcurrency?: number;
  limitCap?: number;
  onBatch?: FetchBatchHandler;
};
type FetchAllUnifiedPlanningFeedOptions = FetchAllOptions & {
  includeShortLets?: boolean;
};

type FetchApplicationByIdOptions = {
  council?: string;
  areaId?: string;
  signal?: AbortSignal;
  retries?: number;
  timeoutMs?: number;
  fetchImpl?: FetchLike;
  headers?: Record<string, string>;
  fresh?: boolean;
};
type FetchMapFeedPageOptions = FetchApplicationByIdOptions & {
  scope?: MapFeedScope;
  limit?: number;
  cursor?: string | null;
  bounds?: PlanningFeedBounds | null;
  community?: string;
  areaId?: string;
};
type FeedSnapshotSource = 'planning' | 'short_term_let';
type FeedSnapshotResponse = {
  applications: Record<string, unknown>[];
  count?: number;
  fetched_at?: string;
  source?: FeedSnapshotSource;
  partial?: boolean;
  error?: Record<string, unknown>;
};
type MapFeedPageResponse = {
  applications: Record<string, unknown>[];
  count: number;
  next_cursor?: string | null;
  scope: MapFeedScope;
  fetched_at?: string;
  partial?: boolean;
};

const DEFAULT_TIMEOUT_MS = 12000;
const DEFAULT_RETRIES = 2;
const API_PAGE_LIMIT = 2000;
const SAFE_FRONTEND_API_LIMIT = 250;
// Production Nginx reserves/rejects the exact bulk limits 500 and 2000.
const SAFE_FRONTEND_MAP_API_LIMIT = 499;
const SAFE_FRONTEND_LIGHT_API_LIMIT = 500;
const LIGHT_FEED_FALLBACK_PAGE_SIZES = [
  SAFE_FRONTEND_LIGHT_API_LIMIT,
  SAFE_FRONTEND_API_LIMIT,
  100,
];
const SAFE_FRONTEND_PAGE_CONCURRENCY = 2;
const PREFER_INTERNAL_LIGHT_FEED_PROXY =
  process.env.NEXT_PUBLIC_LIGHT_FEED_TRANSPORT === 'proxy';
const BASE_URL =
  process.env.NEXT_PUBLIC_APPLICATIONS_API_URL ||
  process.env.APPLICATIONS_API_URL ||
  'https://api.city-scope.co.uk/applications';
const SHORT_TERM_LETS_BASE_URL =
  process.env.NEXT_PUBLIC_SHORT_TERM_LETS_API_URL ||
  process.env.SHORT_TERM_LETS_API_URL ||
  'https://api.city-scope.co.uk/short-term-lets';
const SITE_BASE_URL =
  process.env.NEXT_PUBLIC_SITE_URL ||
  process.env.SITE_URL ||
  'https://www.city-scope.co.uk';
const DEMO_MODE_ENABLED =
  process.env.NEXT_PUBLIC_DEMO_MODE === '1' ||
  process.env.DEMO_MODE === '1';
const DEMO_FIXTURE_PUBLIC_PATH = '/demo-reel/fixtures/applications-demo.json';
const PLANNING_FEED_LIGHT_SNAPSHOT_PATH = '/api/planning-feed/light';
const PLANNING_FEED_PAGE_PROXY_PATH = PLANNING_FEED_LIGHT_SNAPSHOT_PATH;
const PLANNING_FEED_MAP_PROXY_PATH = '/api/planning-feed/map';
const PLANNING_APPLICATION_DETAIL_PATH = '/api/planning-application/detail';
const SHORT_TERM_LET_REFERENCE_SUFFIXES = ['/FULSTL', '/CLESTL', '/CLPSTL'];
const MAP_FEED_BROWSER_CACHE_NAME = 'city-scope-map-feed-v4';
const MAP_FEED_BROWSER_CACHE_TTL_MS = 1000 * 60 * 2;
const MAP_FEED_BROWSER_CACHE_TIMESTAMP_HEADER = 'x-city-scope-cached-at';
const PLANNING_FEED_PAGE_CACHE_TTL_MS = 30_000;
const MAP_FEED_SESSION_STORAGE_KEY = 'city-scope-map-feed-session-v1';

let demoFixtureCache: Record<string, unknown>[] | null = null;
let mapFeedSessionId: string | null = null;
const planningFeedPageCache = new Map<string, { page: PlanningFeedLightPage; fetchedAt: number }>();
const planningFeedPageInFlight = new Map<string, Promise<PlanningFeedLightPage>>();

const getMapFeedSessionId = () => {
  if (typeof window === 'undefined') return undefined;
  if (mapFeedSessionId) return mapFeedSessionId;

  try {
    const stored = window.sessionStorage.getItem(MAP_FEED_SESSION_STORAGE_KEY);
    if (stored && /^[a-z0-9_-]{16,96}$/i.test(stored)) {
      mapFeedSessionId = stored;
      return mapFeedSessionId;
    }
  } catch {
    // Session storage is an optimization for fair admission control only.
  }

  const randomPart = typeof window.crypto?.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  mapFeedSessionId = randomPart.replace(/[^a-z0-9_-]/gi, '').slice(0, 96);

  try {
    window.sessionStorage.setItem(MAP_FEED_SESSION_STORAGE_KEY, mapFeedSessionId);
  } catch {
    // Session storage is not required for the map to function.
  }

  return mapFeedSessionId;
};

const withDirectBrowserApiHeaders = <T extends FetchApplicationsOptions>(
  url: string,
  options: T
): T => {
  if (typeof window === 'undefined') return options;

  try {
    if (new URL(url).origin === window.location.origin) return options;
  } catch {
    return options;
  }

  const sessionId = getMapFeedSessionId();
  return {
    ...options,
    headers: {
      ...options.headers,
      'X-CityScope-Interactive': '1',
      ...(sessionId ? { 'X-CityScope-Map-Session': sessionId } : {}),
    },
  };
};

const shouldTryBrowserProxyFallback = (error: unknown): boolean => {
  if (rateLimitRetryAt(error) !== null) return false;
  if (!(error instanceof ApplicationsApiError)) return true;
  if (error.kind === 'cancelled' || error.kind === 'validation') return false;
  if (error.kind === 'http') {
    return Number(error.status) >= 500;
  }
  return true;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type QueryValue = string | number | string[] | undefined;

const appendUrlQueryValue = (url: URL, key: string, value: QueryValue) => {
  if (Array.isArray(value)) {
    value.forEach((item) => url.searchParams.append(key, item));
    return;
  }
  if (value !== undefined && value !== null) {
    url.searchParams.set(key, String(value));
  }
};

const createUrl = (path = '', query: Record<string, QueryValue> = {}) => {
  refuseLondonLegacyRequest(query.council);
  const normalizedPath = path ? `/${path.replace(/^\/+/, '')}` : '';
  const url = new URL(`${BASE_URL}${normalizedPath}`);
  Object.entries(query).forEach(([key, value]) => appendUrlQueryValue(url, key, value));
  return url.toString();
};

const createShortTermLetsUrl = (path = '', query: Record<string, string | number | undefined> = {}) => {
  refuseLondonLegacyRequest(query.council);
  const normalizedPath = path ? `/${path.replace(/^\/+/, '')}` : '';
  const url = new URL(`${SHORT_TERM_LETS_BASE_URL}${normalizedPath}`);
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
};

const createInternalUrl = (
  path: string,
  query: Record<string, QueryValue> = {}
) => {
  refuseLondonLegacyRequest(query.council);
  const base =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : SITE_BASE_URL;
  const url = new URL(path, base);
  Object.entries(query).forEach(([key, value]) => appendUrlQueryValue(url, key, value));
  return url.toString();
};

function refuseLondonLegacyRequest(council: unknown) {
  if (isLondonScope(council)) {
    throw new ApplicationsApiError('Use the London source transport for this council', 'validation', LONDON_API_ORIGIN, 400);
  }
}

const normalizeRequestedLimit = (value?: number, maxLimit = SAFE_FRONTEND_API_LIMIT): number | undefined => {
  if (!Number.isFinite(value)) return undefined;
  return Math.max(1, Math.min(maxLimit, Number(value)));
};

const fetchBrowserLightFeedPage = async (
  directUrl: string,
  proxyUrl: string,
  options: FetchApplicationsOptions = {}
): Promise<Record<string, unknown>[]> => {
  const primaryUrl = PREFER_INTERNAL_LIGHT_FEED_PROXY ? proxyUrl : directUrl;
  const fallbackUrl = PREFER_INTERNAL_LIGHT_FEED_PROXY ? directUrl : proxyUrl;

  try {
    const data = await requestJson<unknown>(
      primaryUrl,
      withDirectBrowserApiHeaders(primaryUrl, options)
    );
    return ensureApplicationsFeedArray(data, primaryUrl);
  } catch (primaryError) {
    if (!shouldTryBrowserProxyFallback(primaryError)) {
      throw primaryError;
    }
    let skipExternalFallback = false;
    if (typeof window !== 'undefined' && PREFER_INTERNAL_LIGHT_FEED_PROXY) {
      try {
        skipExternalFallback =
          new URL(primaryUrl).origin === window.location.origin &&
          new URL(fallbackUrl).origin !== window.location.origin;
      } catch {
        skipExternalFallback = false;
      }
    }
    if (skipExternalFallback) {
      // Same-origin proxy failed; avoid CORS noise from a doomed browser → api.* fetch.
      throw primaryError;
    }
    const data = await requestJson<unknown>(
      fallbackUrl,
      withDirectBrowserApiHeaders(fallbackUrl, options)
    );
    return ensureApplicationsFeedArray(data, fallbackUrl);
  }
};

async function fetchAllPages(
  fetchPage: (options: FetchApplicationsOptions) => Promise<Record<string, unknown>[]>,
  options: FetchAllOptions = {}
): Promise<Record<string, unknown>[]> {
  const {
    pageSize: requestedPageSize,
    maxPages: requestedMaxPages,
    pageConcurrency: requestedPageConcurrency,
    limitCap: requestedLimitCap,
    onBatch,
    ...fetchPageOptions
  } = options;
  const pageSize =
    normalizeRequestedLimit(
      requestedPageSize ?? API_PAGE_LIMIT,
      requestedLimitCap ?? SAFE_FRONTEND_API_LIMIT
    ) ?? SAFE_FRONTEND_API_LIMIT;
  const maxPages = Math.max(1, requestedMaxPages ?? 10);
  const pageConcurrency = Math.max(
    1,
    Math.min(SAFE_FRONTEND_PAGE_CONCURRENCY, Math.floor(requestedPageConcurrency ?? 1))
  );
  const results: Record<string, unknown>[] = [];
  let step = pageSize;
  const requestPage = (offset: number, limit = pageSize) =>
    fetchPage({
      ...fetchPageOptions,
      limit,
      offset,
    });
  const requestPageWithRecovery = async (offset: number) => {
    let lastError: unknown = null;
    const maxPageAttempts = Number(fetchPageOptions.retries) === 0 ? 1 : 3;
    for (let attempt = 0; attempt < maxPageAttempts; attempt += 1) {
      try {
        return await requestPage(offset);
      } catch (error) {
        if (rateLimitRetryAt(error) !== null) throw error;
        lastError = error;
        if (attempt < maxPageAttempts - 1) {
          await delay(250 * (attempt + 1));
        }
      }
    }

    if (pageSize <= 50) {
      throw lastError;
    }
    if (Number(fetchPageOptions.retries) === 0 && pageSize > SAFE_FRONTEND_API_LIMIT) {
      throw lastError;
    }

    const chunkSize = 50;
    const chunkedResults: Record<string, unknown>[] = [];
    for (let chunkOffset = offset; chunkOffset < offset + pageSize; chunkOffset += chunkSize) {
      const chunkLimit = Math.min(chunkSize, offset + pageSize - chunkOffset);
      let chunk: Record<string, unknown>[] = [];
      let chunkError: unknown = null;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          chunk = await requestPage(chunkOffset, chunkLimit);
          chunkError = null;
          break;
        } catch (error) {
          if (rateLimitRetryAt(error) !== null) throw error;
          chunkError = error;
          if (attempt === 0) {
            await delay(200);
          }
        }
      }

      if (chunkError) {
        if (chunkedResults.length > 0) {
          return chunkedResults;
        }
        throw chunkError;
      }

      if (!Array.isArray(chunk) || chunk.length === 0) {
        break;
      }

      chunkedResults.push(...chunk);
      if (chunk.length < chunkLimit) {
        break;
      }
    }

    return chunkedResults;
  };

  const firstBatch = await requestPageWithRecovery(0);
  results.push(...firstBatch);
  if (firstBatch.length > 0 && typeof onBatch === 'function') {
    await onBatch(firstBatch, { accumulatedCount: results.length });
  }
  if (firstBatch.length === 0 || maxPages === 1) {
    return results;
  }
  if (firstBatch.length < pageSize) {
    // Some live API paths enforce a smaller server-side cap than the requested
    // limit. Follow the observed batch size so later offsets walk the full
    // dataset correctly.
    step = firstBatch.length;
  }

  let offset = step;
  let pagesFetched = 1;

  while (pagesFetched < maxPages) {
    const remainingPages = maxPages - pagesFetched;
    const windowSize = Math.min(pageConcurrency, remainingPages);
    const offsets = Array.from({ length: windowSize }, (_, index) => offset + (index * step));
    const batches = await Promise.all(offsets.map((value) => requestPageWithRecovery(value)));
    let reachedEnd = false;

    for (const batch of batches) {
      if (batch.length === 0) {
        reachedEnd = true;
        break;
      }
      results.push(...batch);
      if (typeof onBatch === 'function') {
        await onBatch(batch, { accumulatedCount: results.length });
      }
      if (batch.length < step) {
        reachedEnd = true;
        break;
      }
    }

    pagesFetched += windowSize;
    if (reachedEnd) {
      break;
    }
    offset += windowSize * step;
  }

  return results;
}

async function fetchAllLightPages(
  fetchPage: (options: FetchApplicationsOptions) => Promise<Record<string, unknown>[]>,
  options: FetchAllOptions = {}
): Promise<Record<string, unknown>[]> {
  const requestedPageSize = Number.isFinite(options.pageSize) ? Number(options.pageSize) : undefined;
  const orderedPageSizes = Array.from(new Set(
    [
      requestedPageSize,
      ...LIGHT_FEED_FALLBACK_PAGE_SIZES,
    ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0)
  ));

  let lastError: unknown = null;

  for (const pageSize of orderedPageSizes) {
    try {
      return await fetchAllPages(fetchPage, {
        ...options,
        pageSize,
        limitCap: pageSize,
        retries: pageSize > SAFE_FRONTEND_API_LIMIT ? 0 : options.retries,
      });
    } catch (error) {
      if (rateLimitRetryAt(error) !== null) throw error;
      lastError = error;
    }
  }

  throw lastError ?? new Error('Failed to fetch light feed pages');
}

const inferShortTermLetApplicationType = (referenceNo: unknown) => {
  const normalized = String(referenceNo ?? '').trim().toUpperCase();
  if (normalized.endsWith('/CLESTL')) return 'Short-Let Certificate of Lawfulness';
  if (normalized.endsWith('/CLPSTL')) return 'Short-Let Proposed Certificate of Lawfulness';
  if (normalized.endsWith('/FULSTL')) return 'Short-Let Planning Permission';
  return 'Short-Let Application';
};

const isShortTermLetReference = (referenceNo: unknown) => {
  const normalized = String(referenceNo ?? '').trim().toUpperCase();
  return SHORT_TERM_LET_REFERENCE_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
};

const annotateShortTermLetRecord = (record: Record<string, unknown>) => ({
  ...record,
  record_type: 'short_term_let',
  data_source: 'short_term_let',
  category: 'short_term_lets',
  application_type: record.application_type || inferShortTermLetApplicationType(record.reference_no),
});

const annotatePlanningRecord = (record: Record<string, unknown>) => ({
  ...record,
  record_type: 'planning',
  data_source: 'planning',
});

const dedupeByReference = (records: Record<string, unknown>[]) => {
  const byReference = new Map<string, Record<string, unknown>>();
  records.forEach((record) => {
    const key = applicationIdentityKey(record) || normalizeLookupValue(
      record.reference_no || record.reference || record.ref || record.application_ref || record.id
    );
    if (!key) return;
    if (!byReference.has(key)) {
      byReference.set(key, record);
    }
  });
  return Array.from(byReference.values());
};

const toTimestamp = (record: Record<string, unknown>) => {
  const candidate = record.created_at || record.scraped_date || record.validated_date || record.received_date;
  if (!candidate) return 0;
  const parsed = new Date(String(candidate)).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortByRecency = (records: Record<string, unknown>[]) =>
  [...records].sort((left, right) => {
    const diff = toTimestamp(right) - toTimestamp(left);
    if (diff !== 0) return diff;
    return String(right.reference_no || '').localeCompare(String(left.reference_no || ''));
  });

const classifyThrownError = (error: unknown, url: string, callerSignal?: AbortSignal) => {
  if (error instanceof ApplicationsApiError) {
    return error;
  }
  if (error instanceof Error && error.name === 'AbortError' && callerSignal?.aborted) {
    return new ApplicationsApiError(`Request was cancelled for ${url}`, 'cancelled', url, undefined, error);
  }
  if (error instanceof Error && (error.name === 'AbortError' || /timed out/i.test(error.message))) {
    return new ApplicationsApiError(`Request timed out for ${url}`, 'timeout', url, undefined, error);
  }
  return new ApplicationsApiError(`Network request failed for ${url}`, 'network', url, undefined, error);
};

// A rate limit is a request to stop. Recovery at another page size or transport
// only adds pressure; callers decide when to refresh using Retry-After.
const shouldRetryStatus = (status: number) => status >= 500;

const mergeSignals = (signalA?: AbortSignal, signalB?: AbortSignal): AbortSignal => {
  if (!signalA && !signalB) {
    return new AbortController().signal;
  }
  if (!signalA) return signalB as AbortSignal;
  if (!signalB) return signalA;

  const controller = new AbortController();
  const onAbort = () => controller.abort();
  signalA.addEventListener('abort', onAbort, { once: true });
  signalB.addEventListener('abort', onAbort, { once: true });
  return controller.signal;
};

async function requestJson<T>(
  url: string,
  {
    retries = DEFAULT_RETRIES,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    signal,
    fetchImpl,
    cache = 'no-store',
    headers,
  }: {
    retries?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
    fetchImpl?: FetchLike;
    cache?: RequestCache;
    headers?: Record<string, string>;
  }
): Promise<T> {
  const effectiveFetch = fetchImpl || fetch;
  let lastError: ApplicationsApiError | null = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
    const requestSignal = mergeSignals(signal, timeoutController.signal);

    try {
      const response = await effectiveFetch(url, {
        signal: requestSignal,
        cache,
        ...(headers ? { headers } : {}),
      });

      if (!response.ok) {
        const apiError = new ApplicationsApiError(
          `Applications API request failed (${response.status})`,
          'http',
          url,
          response.status
        );
        apiError.retryAfterMs = parseRetryAfterMs(response.headers?.get('Retry-After'));
        if (!shouldRetryStatus(response.status) || attempt >= retries) {
          throw apiError;
        }
        lastError = apiError;
      } else {
        try {
          return (await response.json()) as T;
        } catch (parseError) {
          throw new ApplicationsApiError(
            `Failed to parse applications API response from ${url}`,
            'validation',
            url,
            response.status,
            parseError
          );
        }
      }
    } catch (error: unknown) {
      const classifiedError = classifyThrownError(error, url, signal);
      const canRetry =
        classifiedError.kind === 'network' ||
        classifiedError.kind === 'timeout' ||
        (classifiedError.kind === 'http' && shouldRetryStatus(classifiedError.status || 0));

      if (!canRetry || attempt >= retries) {
        throw classifiedError;
      }

      lastError = classifiedError;
    } finally {
      clearTimeout(timeoutId);
    }

    const backoff = Math.min(1000 * Math.pow(2, attempt), 4000);
    await delay(backoff);
  }

  throw (
    lastError ||
    new ApplicationsApiError('Unknown applications API error', 'network', url)
  );
}

function ensureApplicationsArray(data: unknown, url: string): Record<string, unknown>[] {
  if (!Array.isArray(data)) {
    throw new ApplicationsApiError('Expected applications API to return an array', 'validation', url);
  }
  return data.filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null);
}

function ensureApplicationsFeedArray(data: unknown, url: string): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return ensureApplicationsArray(data, url);
  }
  if (
    data &&
    typeof data === 'object' &&
    Array.isArray((data as { applications?: unknown }).applications)
  ) {
    return ensureApplicationsArray((data as { applications: unknown[] }).applications, url);
  }
  throw new ApplicationsApiError('Expected applications API to return an array or feed payload', 'validation', url);
}

function ensurePlanningFeedLightPage(data: unknown, url: string): PlanningFeedLightPage {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new ApplicationsApiError('Planning feed page was not an object', 'validation', url);
  }

  const payload = data as Record<string, unknown>;
  const query = new URL(url).searchParams;
  const emptyPartial = payload.partial === true && Array.isArray(payload.applications) &&
    payload.applications.length === 0 && Number(payload.total || 0) === 0;
  if (query.get('country') === SCOTTISH_READ_MODEL_COUNTRY && !confirmsScottishReadModelScope(payload) &&
      !(emptyPartial && payload.scope == null)) {
    throw new ApplicationsApiError('Planning feed did not confirm Scotland scope', 'validation', url);
  }
  if (!emptyPartial && (query.has('validated_from') || query.has('validated_end_exclusive')) &&
      (payload.date_basis !== 'validated' || payload.window_start !== query.get('validated_from') ||
       payload.window_end_exclusive !== query.get('validated_end_exclusive'))) {
    throw new ApplicationsApiError('Planning feed did not confirm validation dates', 'validation', url);
  }
  const applications = ensureApplicationsFeedArray(payload, url).map(annotatePlanningRecord);
  const total = Number(payload.total);
  const count = Number(payload.count);

  return {
    applications,
    count: Number.isFinite(count) ? count : applications.length,
    total: Number.isFinite(total) ? total : applications.length,
    next_cursor: typeof payload.next_cursor === 'string' && payload.next_cursor
      ? payload.next_cursor
      : null,
    facets: payload.facets && typeof payload.facets === 'object' && !Array.isArray(payload.facets)
      ? payload.facets as PlanningFeedFacets
      : {},
    fetched_at: typeof payload.fetched_at === 'string' ? payload.fetched_at : undefined,
    data_version: typeof payload.data_version === 'number' || typeof payload.data_version === 'string'
      ? payload.data_version
      : null,
    partial: payload.partial === true,
    ...(payload.date_basis === 'validated' ? {
      date_basis: 'validated' as const,
      window_start: String(payload.window_start),
      window_end_exclusive: String(payload.window_end_exclusive),
    } : {}),
  };
}

const normalizeLookupValue = (value: unknown) => String(value ?? '').trim().toLowerCase();

async function loadDemoFixture(fetchImpl?: FetchLike): Promise<Record<string, unknown>[]> {
  if (demoFixtureCache) return demoFixtureCache;

  let data: unknown;
  if (typeof window === 'undefined') {
    const fs = await import(/* webpackIgnore: true */ 'node:fs/promises');
    const path = await import(/* webpackIgnore: true */ 'node:path');
    const fixturePath = path.join(process.cwd(), 'public', 'demo-reel', 'fixtures', 'applications-demo.json');
    const raw = await fs.readFile(fixturePath, 'utf8');
    data = JSON.parse(raw);
  } else {
    const effectiveFetch = fetchImpl || fetch;
    const response = await effectiveFetch(DEMO_FIXTURE_PUBLIC_PATH);
    if (!response.ok) {
      throw new ApplicationsApiError(
        `Failed to load demo fixture (${response.status})`,
        'http',
        DEMO_FIXTURE_PUBLIC_PATH,
        response.status
      );
    }
    data = await response.json();
  }

  demoFixtureCache = ensureApplicationsArray(data, DEMO_FIXTURE_PUBLIC_PATH);
  return demoFixtureCache;
}

export async function fetchApplications(
  options: FetchApplicationsOptions = {}
): Promise<Record<string, unknown>[]> {
  if (usesSharedLondonDelivery(options.council)) return (await fetchPlanningFeedLightPage(options)).applications;
  if (DEMO_MODE_ENABLED) {
    const fixture = await loadDemoFixture(options.fetchImpl);
    const offset = Number.isFinite(options.offset) ? Math.max(0, Number(options.offset)) : 0;
    const limit = Number(options.limit);
    if (Number.isFinite(limit) && limit >= 0) {
      return fixture.slice(offset, offset + limit);
    }
    return fixture.slice(offset);
  }

  const url = createUrl('', {
    limit: normalizeRequestedLimit(options.limit),
    offset: options.offset,
    council: options.council,
    community: options.community,
    area_id: options.areaId,
  });

  if (typeof window !== 'undefined') {
    const proxyUrl = createInternalUrl(PLANNING_FEED_PAGE_PROXY_PATH, {
      source: 'planning',
      variant: 'map',
      limit: normalizeRequestedLimit(options.limit),
      offset: options.offset,
      council: options.council,
      community: options.community,
    });

    return fetchBrowserLightFeedPage(url, proxyUrl, options);
  }

  const data = await requestJson<unknown>(url, options);
  return ensureApplicationsArray(data, url);
}

export async function fetchApplicationsLight(
  options: FetchApplicationsLightOptions = {}
): Promise<Record<string, unknown>[]> {
  if (usesSharedLondonDelivery(options.council)) return (await fetchPlanningFeedLightPage(options)).applications;
  if (DEMO_MODE_ENABLED) {
    const data = await fetchApplications(options);
    return data.map((item) => ({
      reference_no: item.reference_no,
      ref: item.ref,
      address: item.address,
      received_date: item.received_date,
      validated_date: item.validated_date,
      scraped_date: item.scraped_date,
      created_at: item.created_at,
      category: item.category,
      interesting: item.interesting,
      interest_confidence: item.interest_confidence,
      interest_reason: item.interest_reason,
      interest_signals: item.interest_signals,
    }));
  }

  const limit = normalizeRequestedLimit(options.limit, SAFE_FRONTEND_LIGHT_API_LIMIT);
  const aggregateRequest = !options.council || isAggregateCouncilScope(options.council);
  const directUrl = createUrl(aggregateRequest ? 'unified-light' : 'light', {
    limit,
    offset: options.offset,
    q: options.query,
    source: aggregateRequest ? 'planning' : undefined,
    council: aggregateRequest ? undefined : options.council,
    community: options.community,
    area_id: options.areaId,
  });

  if (typeof window === 'undefined') {
    const data = await requestJson<unknown>(directUrl, options);
    return ensureApplicationsFeedArray(data, directUrl);
  }

  const proxyUrl = createInternalUrl(PLANNING_FEED_PAGE_PROXY_PATH, {
    source: 'planning',
    variant: 'light',
    limit,
    offset: options.offset,
    q: options.query,
    council: options.council,
    community: options.community,
    area_id: options.areaId,
  });

  return fetchBrowserLightFeedPage(directUrl, proxyUrl, options);
}

export type PlanningFeedSummary = {
  counts: { total: number; today: number; yesterday: number; recent: number; interesting: number };
  business_date: string;
  council: string | null;
  area_id: string | null;
  fetched_at: string | null;
};

export async function fetchPlanningFeedSummary(options: FetchApplicationsOptions & { day: string; areaId?: string }): Promise<PlanningFeedSummary> {
  if (usesSharedLondonDelivery(options.council)) {
    try { return await readSharedLondonPayload('summary',{
      council:options.council,day:options.day,...(options.areaId ? { area_id:options.areaId } : {}) },options); }
    catch (error) {
      if (error instanceof SharedLondonError) throw new ApplicationsApiError(error.message,'http',LONDON_API_ORIGIN,error.status,error);
      throw error;
    }
  }
  const url = new URL('/stats/planning-feed-summary', BASE_URL);
  const council = isAggregateCouncilScope(options.council) ? null : options.council || null;
  if (council) url.searchParams.set('council', council);
  else url.searchParams.set('country', SCOTTISH_READ_MODEL_COUNTRY);
  if (options.areaId) url.searchParams.set('area_id', options.areaId);
  // Also separates the CDN cache at London midnight.
  url.searchParams.set('day', options.day);
  const payload = await requestJson<PlanningFeedSummary>(url.toString(), withDirectBrowserApiHeaders(url.toString(), options));
  const keys = ['total', 'today', 'yesterday', 'recent', 'interesting'] as const;
  if (!payload || payload.business_date !== options.day || payload.council !== council ||
      (payload.area_id || null) !== (options.areaId || null) || (!council && !confirmsScottishReadModelScope(payload)) ||
      keys.some(key => !Number.isInteger(payload.counts?.[key]) || payload.counts[key] < 0)) {
    throw new ApplicationsApiError('Invalid or stale planning summary', 'validation', url.toString());
  }
  return payload;
}

export async function fetchPlanningFeedLightPage(
  options: FetchPlanningFeedLightPageOptions = {}
): Promise<PlanningFeedLightPage> {
  if (isLondonScope(options.council)) {
    const shared=usesSharedLondonDelivery(options.council);
    if (!shared) rejectUnsupportedLondonFilters(options);
    if (shared && ((options.offset != null && options.offset !== 0) || options.includeShortLets)) {
      throw new ApplicationsApiError('London planning pages require a keyset cursor and planning source', 'validation', LONDON_API_ORIGIN,400);
    }
    if (options.limit !== undefined && (!Number.isInteger(options.limit) || options.limit < 1)) {
      throw new ApplicationsApiError('Invalid London page size', 'validation', LONDON_API_ORIGIN, 400);
    }
    return londonOperation(() => readLondonFeed({
      council: options.council, query: options.query, cursor: options.cursor,
      limit: options.limit === undefined ? 40 : Math.min(options.limit, shared ? 500 : 100),
      start: options.validatedFrom, end: options.validatedEndExclusive,
      bounds: options.bounds ? { west: options.bounds.min_lng, south: options.bounds.min_lat,
        east: options.bounds.max_lng, north: options.bounds.max_lat } : undefined,
      fetchImpl: options.fetchImpl, signal: options.signal,
      boundsEndExclusive: options.boundsEndExclusive,
      ...(shared ? { filters: {
        ...sharedLondonFilters(options),
        include_facets:options.includeFacets,include_total:options.includeTotal,
      } } : {}),
    }));
  }
  const limit = normalizeRequestedLimit(options.limit, SAFE_FRONTEND_LIGHT_API_LIMIT) || 250;
  const council = isAggregateCouncilScope(options.council) ? undefined : options.council;
  const sharedQuery = {
    source: options.includeShortLets ? 'all' : 'planning',
    variant: 'light',
    response_shape: 'envelope',
    limit,
    offset: options.cursor ? undefined : options.offset,
    cursor: options.cursor || undefined,
    q: options.query,
    council,
    country: council ? undefined : SCOTTISH_READ_MODEL_COUNTRY,
    community: options.community,
    area_id: options.areaId,
    category_value: options.categories?.length ? options.categories : undefined,
    exclude_category_value: options.excludeCategories?.length ? options.excludeCategories : undefined,
    tags: options.tags?.join(',') || undefined,
    status: options.statuses?.join(',') || undefined,
    decision_status: options.decisionStatus || undefined,
    postcode_prefixes: options.postcodePrefixes?.join(',') || undefined,
    has_documents: options.hasDocuments === null || options.hasDocuments === undefined
      ? undefined
      : String(options.hasDocuments),
    has_forms: options.hasForms === null || options.hasForms === undefined
      ? undefined
      : String(options.hasForms),
    impact_min: options.impactMin ?? undefined,
    impact_max: options.impactMax ?? undefined,
    activity_from: options.activityFrom || undefined,
    activity_to: options.activityTo || undefined,
    added_from: options.addedFrom || undefined,
    added_to: options.addedTo || undefined,
    interesting: options.interesting === null || options.interesting === undefined
      ? undefined
      : String(options.interesting),
    min_lng: options.bounds?.min_lng,
    min_lat: options.bounds?.min_lat,
    max_lng: options.bounds?.max_lng,
    max_lat: options.bounds?.max_lat,
    center_lng: options.centerLng ?? undefined,
    center_lat: options.centerLat ?? undefined,
    radius_km: options.radiusKm ?? undefined,
    include_facets: options.includeFacets ? '1' : undefined,
    include_total: options.includeTotal === false ? '0' : undefined,
    validated_from: options.validatedFrom,
    validated_end_exclusive: options.validatedEndExclusive,
  };
  const directUrl = createUrl('unified-light', sharedQuery);
  const proxyUrl = createInternalUrl(PLANNING_FEED_PAGE_PROXY_PATH, {
    ...sharedQuery,
    council: options.council,
  });

  if (typeof window === 'undefined') {
    return ensurePlanningFeedLightPage(await requestJson<unknown>(directUrl, options), directUrl);
  }

  const primaryUrl = PREFER_INTERNAL_LIGHT_FEED_PROXY ? proxyUrl : directUrl;
  const fallbackUrl = PREFER_INTERNAL_LIGHT_FEED_PROXY ? directUrl : proxyUrl;

  const fetchPage = async (requestOptions: FetchPlanningFeedLightPageOptions) => {
    try {
      return ensurePlanningFeedLightPage(
        await requestJson<unknown>(
          primaryUrl,
          withDirectBrowserApiHeaders(primaryUrl, requestOptions)
        ),
        primaryUrl
      );
    } catch (primaryError) {
      if (!shouldTryBrowserProxyFallback(primaryError)) {
        throw primaryError;
      }
      const primaryIsSameOrigin = new URL(primaryUrl).origin === window.location.origin;
      const fallbackIsCrossOrigin = new URL(fallbackUrl).origin !== window.location.origin;
      if (primaryIsSameOrigin && fallbackIsCrossOrigin) {
        throw primaryError;
      }
      return ensurePlanningFeedLightPage(
        await requestJson<unknown>(
          fallbackUrl,
          withDirectBrowserApiHeaders(fallbackUrl, requestOptions)
        ),
        fallbackUrl
      );
    }
  };

  // Tests and callers with an injected transport retain one-request-per-call semantics.
  if (options.fetchImpl) {
    return fetchPage(options);
  }

  const now = Date.now();
  const cached = planningFeedPageCache.get(primaryUrl);
  if (cached && now - cached.fetchedAt < PLANNING_FEED_PAGE_CACHE_TTL_MS) {
    return cached.page;
  }
  if (cached) {
    planningFeedPageCache.delete(primaryUrl);
  }

  const activeRequest = planningFeedPageInFlight.get(primaryUrl);
  if (activeRequest) {
    return activeRequest;
  }

  // A shared request is deliberately not tied to either consumer's AbortSignal. Each
  // consumer still discards stale results, while a sibling consumer can reuse the call.
  const request = fetchPage({ ...options, signal: undefined })
    .then((page) => {
      planningFeedPageCache.set(primaryUrl, { page, fetchedAt: Date.now() });
      return page;
    })
    .finally(() => {
      planningFeedPageInFlight.delete(primaryUrl);
    });
  planningFeedPageInFlight.set(primaryUrl, request);
  return request;
}

export async function fetchShortTermLets(
  options: FetchShortTermLetsOptions = {}
): Promise<Record<string, unknown>[]> {
  const url = createShortTermLetsUrl('', {
    limit: normalizeRequestedLimit(options.limit),
    offset: options.offset,
    council: options.council,
    community: options.community,
    area_id: options.areaId,
  });
  const data = await requestJson<unknown>(url, options);
  return ensureApplicationsArray(data, url);
}

export async function fetchAllApplications(
  options: FetchAllOptions = {}
): Promise<Record<string, unknown>[]> {
  if (isLondonScope(options.council)) throw new ApplicationsApiError('London uses bounded keyset pages','validation',LONDON_API_ORIGIN,400);
  return fetchAllPages(fetchApplications, options);
}

export async function fetchAllApplicationsLight(
  options: FetchAllOptions = {}
): Promise<Record<string, unknown>[]> {
  if (isLondonScope(options.council)) throw new ApplicationsApiError('London uses bounded keyset pages','validation',LONDON_API_ORIGIN,400);
  return fetchAllLightPages(fetchApplicationsLight, options);
}

export async function fetchShortTermLetsLight(
  options: FetchShortTermLetsLightOptions = {}
): Promise<Record<string, unknown>[]> {
  const limit = normalizeRequestedLimit(options.limit, SAFE_FRONTEND_LIGHT_API_LIMIT);
  const directUrl = createShortTermLetsUrl('light', {
    limit,
    offset: options.offset,
    council: options.council,
    community: options.community,
    area_id: options.areaId,
  });

  if (typeof window === 'undefined') {
    const data = await requestJson<unknown>(directUrl, options);
    return ensureApplicationsArray(data, directUrl);
  }

  const proxyUrl = createInternalUrl(PLANNING_FEED_PAGE_PROXY_PATH, {
    source: 'short_term_let',
    limit,
    offset: options.offset,
    council: options.council,
    community: options.community,
    area_id: options.areaId,
  });

  return fetchBrowserLightFeedPage(directUrl, proxyUrl, options);
}

export async function fetchAllShortTermLets(
  options: FetchAllOptions = {}
): Promise<Record<string, unknown>[]> {
  return fetchAllPages(fetchShortTermLets, options);
}

export async function fetchAllShortTermLetsLight(
  options: FetchAllOptions = {}
): Promise<Record<string, unknown>[]> {
  return fetchAllLightPages(fetchShortTermLetsLight, options);
}

export async function fetchFeedLightSnapshot(
  source: FeedSnapshotSource,
  options: FetchApplicationByIdOptions = {}
): Promise<Record<string, unknown>[]> {
  const url = createInternalUrl(PLANNING_FEED_LIGHT_SNAPSHOT_PATH, {
    source,
    council: options.council,
    area_id: options.areaId,
  });
  const data = await requestJson<FeedSnapshotResponse | unknown>(url, options);

  if (Array.isArray(data)) {
    return ensureApplicationsArray(data, url);
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return ensureApplicationsArray((data as FeedSnapshotResponse).applications, url);
  }

  throw new ApplicationsApiError(
    'Expected planning feed snapshot to return an applications array',
    'validation',
    url
  );
}

export async function fetchPlanningFeedLightSnapshot(
  options: FetchApplicationByIdOptions = {}
): Promise<Record<string, unknown>[]> {
  return fetchFeedLightSnapshot('planning', options);
}

export async function fetchShortTermLetsLightSnapshot(
  options: FetchApplicationByIdOptions = {}
): Promise<Record<string, unknown>[]> {
  return fetchFeedLightSnapshot('short_term_let', options);
}

const parseMapFeedSnapshotData = (data: FeedSnapshotResponse | unknown, url: string) => {
  if (Array.isArray(data)) {
    return ensureApplicationsArray(data, url);
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const response = data as FeedSnapshotResponse;
    const applications = ensureApplicationsArray(response.applications, url);
    if (response.partial) {
      Object.defineProperty(applications, 'partial', {
        value: true,
        enumerable: false,
      });
      Object.defineProperty(applications, 'snapshotError', {
        value: response.error,
        enumerable: false,
      });
    }
    return applications;
  }

  throw new ApplicationsApiError(
    'Expected planning map feed snapshot to return an applications array',
    'validation',
    url
  );
};

const parseMapFeedPageData = (data: MapFeedPageResponse | unknown, url: string): MapFeedPageResponse => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new ApplicationsApiError(
      'Expected planning map page feed to return an object',
      'validation',
      url
    );
  }

  const response = data as Partial<MapFeedPageResponse>;
  const applications = ensureApplicationsArray(response.applications, url);
  return {
    applications,
    count: Number.isFinite(Number(response.count)) ? Number(response.count) : applications.length,
    next_cursor: typeof response.next_cursor === 'string' && response.next_cursor.trim()
      ? response.next_cursor
      : null,
    scope: response.scope === 'viewport' ? 'viewport' : 'newest',
    fetched_at: typeof response.fetched_at === 'string' ? response.fetched_at : undefined,
    ...(response.partial === true ? { partial: true } : {}),
  };
};

export async function fetchPlanningMapFeedPage(
  options: FetchMapFeedPageOptions = {}
): Promise<MapFeedPageResponse> {
  if (isLondonScope(options.council)) {
    throw new ApplicationsApiError('London maps require viewport aggregation', 'validation', LONDON_API_ORIGIN, 400);
  }
  const scope: MapFeedScope = options.scope === 'viewport' ? 'viewport' : 'newest';
  const aggregateRequest = isAggregateCouncilScope(options.council);
  const query: Record<string, string | number | undefined> = {
    scope,
    limit: normalizeRequestedLimit(options.limit, SAFE_FRONTEND_MAP_API_LIMIT) ?? 50,
    cursor: options.cursor || undefined,
    // The core /applications/map route defaults an omitted council to
    // Edinburgh. Aggregate requests therefore stay on the same-origin proxy,
    // which targets the public-council materialized read model instead.
    council: aggregateRequest ? undefined : options.council,
    community: options.community,
    area_id: options.areaId,
  };

  if (scope === 'viewport' && options.bounds) {
    query.min_lng = options.bounds.min_lng;
    query.min_lat = options.bounds.min_lat;
    query.max_lng = options.bounds.max_lng;
    query.max_lat = options.bounds.max_lat;
  }

  const directUrl = createUrl('map', query);
  const proxyUrl = createInternalUrl(PLANNING_FEED_MAP_PROXY_PATH, {
    ...query,
    council: aggregateRequest ? options.council : query.council,
  });
  const hasProxyOffsetCursor = typeof options.cursor === 'string' && /^\d+$/.test(options.cursor.trim());
  const primaryUrl = typeof window !== 'undefined' &&
      !aggregateRequest &&
      !PREFER_INTERNAL_LIGHT_FEED_PROXY &&
      !hasProxyOffsetCursor
    ? directUrl
    : proxyUrl;
  const fallbackUrl = primaryUrl === directUrl ? proxyUrl : directUrl;
  const mapSessionId = getMapFeedSessionId();
  const requestOptions = {
    retries: options.retries,
    timeoutMs: options.timeoutMs,
    signal: options.signal,
    fetchImpl: options.fetchImpl,
    cache: 'no-store' as RequestCache,
    headers: {
      ...options.headers,
      ...(mapSessionId ? { 'X-CityScope-Map-Session': mapSessionId } : {}),
    },
  };

  try {
    const data = await requestJson<MapFeedPageResponse | unknown>(
      primaryUrl,
      withDirectBrowserApiHeaders(primaryUrl, requestOptions)
    );
    return parseMapFeedPageData(data, primaryUrl);
  } catch (primaryError) {
    // Falling an aggregate proxy request back to the core route would silently
    // turn Scotland into Edinburgh because that route has a legacy default.
    if (aggregateRequest) {
      throw primaryError;
    }
    // Cursor formats are transport-specific: the backend uses an opaque
    // keyset cursor, while the compatibility proxy uses a numeric offset.
    // Switching transports mid-feed causes 400 loops and duplicate pages.
    if ((primaryUrl === directUrl && options.cursor) || hasProxyOffsetCursor) {
      throw primaryError;
    }
    if (!shouldTryBrowserProxyFallback(primaryError)) {
      throw primaryError;
    }
    if (
      typeof window !== 'undefined' &&
      new URL(primaryUrl).origin === window.location.origin &&
      new URL(fallbackUrl).origin !== window.location.origin
    ) {
      throw primaryError;
    }
    const data = await requestJson<MapFeedPageResponse | unknown>(
      fallbackUrl,
      withDirectBrowserApiHeaders(fallbackUrl, requestOptions)
    );
    return parseMapFeedPageData(data, fallbackUrl);
  }
}

const canUseBrowserMapFeedCache = (options: FetchApplicationByIdOptions) =>
  typeof window !== 'undefined' &&
  typeof caches !== 'undefined' &&
  !options.fetchImpl;

const readBrowserMapFeedCache = async (url: string) => {
  try {
    const cache = await caches.open(MAP_FEED_BROWSER_CACHE_NAME);
    const cachedResponse = await cache.match(url);
    if (!cachedResponse) return null;

    const cachedAt = Number(cachedResponse.headers.get(MAP_FEED_BROWSER_CACHE_TIMESTAMP_HEADER));
    if (!Number.isFinite(cachedAt) || Date.now() - cachedAt > MAP_FEED_BROWSER_CACHE_TTL_MS) {
      return null;
    }

    const cachedData = await cachedResponse.json();
    return parseMapFeedSnapshotData(cachedData, url);
  } catch {
    return null;
  }
};

const writeBrowserMapFeedCache = async (url: string, data: FeedSnapshotResponse | unknown) => {
  try {
    const cache = await caches.open(MAP_FEED_BROWSER_CACHE_NAME);
    await cache.put(
      url,
      new Response(JSON.stringify(data), {
        headers: {
          'Content-Type': 'application/json',
          [MAP_FEED_BROWSER_CACHE_TIMESTAMP_HEADER]: String(Date.now()),
        },
      })
    );
  } catch {
    // Cache writes are an optimization only; the map feed remains usable without them.
  }
};

export async function fetchMapFeedSnapshot(
  source: FeedSnapshotSource,
  options: FetchApplicationByIdOptions = {}
): Promise<Record<string, unknown>[]> {
  const url = createInternalUrl(source === 'planning' ? '/api/planning-feed/catalogue' : PLANNING_FEED_LIGHT_SNAPSHOT_PATH, {
    source,
    variant: 'map',
    council: options.council,
    area_id: options.areaId,
    // Invalidate pre-release catalogues after enabling all 32 councils and mapping their records.
    feed_version: source === 'planning' ? 'compact-v2-public32' : 'documents-v3',
  });

  const useBrowserCache = canUseBrowserMapFeedCache(options);
  if (useBrowserCache) {
    const cachedData = await readBrowserMapFeedCache(url);
    if (cachedData) {
      return cachedData;
    }
  }

  const data = await requestJson<FeedSnapshotResponse | unknown>(url, {
    ...options,
    cache: useBrowserCache ? 'reload' : 'no-store',
  });
  const applications = parseMapFeedSnapshotData(data, url);

  if (useBrowserCache && !(applications as Record<string, unknown>[] & { partial?: boolean }).partial) {
    void writeBrowserMapFeedCache(url, data);
  }

  return applications;
}

export async function fetchPlanningMapFeedSnapshot(
  options: FetchApplicationByIdOptions = {}
): Promise<Record<string, unknown>[]> {
  return fetchMapFeedSnapshot('planning', options);
}

export async function fetchShortTermLetsMapFeedSnapshot(
  options: FetchApplicationByIdOptions = {}
): Promise<Record<string, unknown>[]> {
  return fetchMapFeedSnapshot('short_term_let', options);
}

export async function fetchAllUnifiedPlanningFeed(
  options: FetchAllUnifiedPlanningFeedOptions = {}
): Promise<Record<string, unknown>[]> {
  const { onBatch, ...restOptions } = options;
  const toAnnotatedBatchHandler = (source: FeedSource): FetchBatchHandler | undefined => {
    if (typeof onBatch !== 'function') return undefined;
    return async (batch, info) => {
      const annotated = source === 'planning'
        ? batch.map(annotatePlanningRecord)
        : batch.map(annotateShortTermLetRecord);
      await onBatch(annotated, { ...info, source });
    };
  };

  if (!options.includeShortLets) {
    const planning = await fetchAllApplications({
      ...restOptions,
      onBatch: toAnnotatedBatchHandler('planning'),
    });
    return sortByRecency(planning.map(annotatePlanningRecord));
  }

  const [planning, shortLets] = await Promise.all([
    fetchAllApplications({
      ...restOptions,
      onBatch: toAnnotatedBatchHandler('planning'),
    }),
    fetchAllShortTermLets({
      ...restOptions,
      onBatch: toAnnotatedBatchHandler('short_term_let'),
    }).catch(() => [] as Record<string, unknown>[]),
  ]);

  return sortByRecency(dedupeByReference([
    ...planning.map(annotatePlanningRecord),
    ...shortLets.map(annotateShortTermLetRecord),
  ]));
}

export async function fetchUnifiedPlanningFeed(
  options: FetchUnifiedPlanningFeedOptions = {}
): Promise<Record<string, unknown>[]> {
  if (!options.includeShortLets) {
    const planning = await fetchApplications(options);
    return sortByRecency(planning.map(annotatePlanningRecord));
  }

  const [planning, shortLets] = await Promise.all([
    fetchApplications(options),
    fetchShortTermLets(options).catch(() => [] as Record<string, unknown>[]),
  ]);
  return sortByRecency(dedupeByReference([
    ...planning.map(annotatePlanningRecord),
    ...shortLets.map(annotateShortTermLetRecord),
  ]));
}

export async function fetchUnifiedPlanningFeedLight(
  options: FetchUnifiedPlanningFeedOptions = {}
): Promise<Record<string, unknown>[]> {
  if (!options.includeShortLets) {
    const planning = await fetchApplicationsLight(options);
    return sortByRecency(planning.map(annotatePlanningRecord));
  }

  const [planning, shortLets] = await Promise.all([
    fetchApplicationsLight(options),
    fetchShortTermLetsLight(options).catch(() => [] as Record<string, unknown>[]),
  ]);
  return sortByRecency(dedupeByReference([
    ...planning.map(annotatePlanningRecord),
    ...shortLets.map(annotateShortTermLetRecord),
  ]));
}

export async function fetchUnifiedPlanningMapFeed(
  options: FetchUnifiedPlanningFeedOptions = {}
): Promise<Record<string, unknown>[]> {
  const fetchSource = async (source: FeedSource) => {
    const url = createUrl('unified-light', {
      variant: 'map',
      source,
      limit: normalizeRequestedLimit(options.limit, SAFE_FRONTEND_LIGHT_API_LIMIT),
      offset: options.offset,
      council: options.council,
      community: options.community,
      area_id: options.areaId,
    });
    const data = await requestJson<unknown>(url, options);
    const records = ensureApplicationsFeedArray(data, url);
    return source === 'planning'
      ? records.map(annotatePlanningRecord)
      : records.map(annotateShortTermLetRecord);
  };

  if (!options.includeShortLets) {
    return sortByRecency(await fetchSource('planning'));
  }

  const [planning, shortLets] = await Promise.all([
    fetchSource('planning'),
    fetchSource('short_term_let').catch((error) => {
      if (rateLimitRetryAt(error) !== null) throw error;
      return [] as Record<string, unknown>[];
    }),
  ]);

  return sortByRecency(dedupeByReference([
    ...planning,
    ...shortLets,
  ]));
}

export async function fetchAllUnifiedPlanningMapFeed(
  options: FetchAllUnifiedPlanningFeedOptions = {}
): Promise<Record<string, unknown>[]> {
  return fetchAllLightPages(fetchUnifiedPlanningMapFeed, options);
}

export async function fetchAllUnifiedPlanningFeedLight(
  options: FetchAllUnifiedPlanningFeedOptions = {}
): Promise<Record<string, unknown>[]> {
  const { onBatch, ...restOptions } = options;
  const toAnnotatedBatchHandler = (source: FeedSource): FetchBatchHandler | undefined => {
    if (typeof onBatch !== 'function') return undefined;
    return async (batch, info) => {
      const annotated = source === 'planning'
        ? batch.map(annotatePlanningRecord)
        : batch.map(annotateShortTermLetRecord);
      await onBatch(annotated, { ...info, source });
    };
  };

  if (!options.includeShortLets) {
    const planning = await fetchAllApplicationsLight({
      ...restOptions,
      onBatch: toAnnotatedBatchHandler('planning'),
    });
    return sortByRecency(planning.map(annotatePlanningRecord));
  }

  const [planning, shortLets] = await Promise.all([
    fetchAllApplicationsLight({
      ...restOptions,
      onBatch: toAnnotatedBatchHandler('planning'),
    }),
    fetchAllShortTermLetsLight({
      ...restOptions,
      onBatch: toAnnotatedBatchHandler('short_term_let'),
    }).catch(() => [] as Record<string, unknown>[]),
  ]);

  return sortByRecency(dedupeByReference([
    ...planning.map(annotatePlanningRecord),
    ...shortLets.map(annotateShortTermLetRecord),
  ]));
}

export async function fetchApplicationById(
  id: string | number,
  options: FetchApplicationByIdOptions = {}
): Promise<Record<string, unknown> | null> {
  const londonUid = londonDetailUid(id, options);
  if (londonUid) return londonDetail(londonUid, options);
  if (DEMO_MODE_ENABLED) {
    const fixture = await loadDemoFixture(options.fetchImpl);
    const target = normalizeLookupValue(id);
    const found = fixture.find((item) => {
      const candidates = [
        item.id,
        item.reference_no,
        item.reference,
        item.ref,
        item.application_ref,
      ];
      return candidates.some((candidate) => normalizeLookupValue(candidate) === target);
    });
    return found || null;
  }

  const normalizedId = String(id);
  const shouldUseShortTermLetsApi = isShortTermLetReference(normalizedId);
  const url =
    typeof window !== 'undefined'
      ? createInternalUrl(PLANNING_APPLICATION_DETAIL_PATH, {
          reference: normalizedId,
          council: options.council,
          fresh: options.fresh ? 1 : undefined,
        })
      : shouldUseShortTermLetsApi
        ? createShortTermLetsUrl(normalizedId, { council: options.council })
        : createUrl(normalizedId, { council: options.council });
  try {
    const data = await requestJson<unknown>(url, options);
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new ApplicationsApiError('Expected application detail response to be an object', 'validation', url);
    }
    if ((data as { temporary_unavailable?: unknown }).temporary_unavailable === true) {
      throw new ApplicationsApiError(
        'Application detail temporarily unavailable',
        'http',
        url,
        503
      );
    }
    return shouldUseShortTermLetsApi
      ? annotateShortTermLetRecord(data as Record<string, unknown>)
      : data as Record<string, unknown>;
  } catch (error) {
    if (error instanceof ApplicationsApiError && error.kind === 'http' && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Fetch a detail through City Scope's same-origin recovery/cache proxy.
 * Server-rendered pages use this explicitly so they share the browser/API
 * route's stale-cache and transient-recovery behaviour without recursing.
 */
export async function fetchApplicationByIdViaDetailProxy(
  id: string | number,
  options: FetchApplicationByIdOptions = {}
): Promise<Record<string, unknown> | null> {
  const londonUid = londonDetailUid(id, options);
  if (londonUid) return londonDetail(londonUid, options);
  const normalizedId = String(id);
  const url = createInternalUrl(PLANNING_APPLICATION_DETAIL_PATH, {
    reference: normalizedId,
    council: options.council,
    fresh: options.fresh ? 1 : undefined,
  });

  try {
    const data = await requestJson<unknown>(url, options);
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new ApplicationsApiError('Expected application detail response to be an object', 'validation', url);
    }
    if ((data as { temporary_unavailable?: unknown }).temporary_unavailable === true) {
      throw new ApplicationsApiError(
        'Application detail temporarily unavailable',
        'http',
        url,
        503
      );
    }
    return data as Record<string, unknown>;
  } catch (error) {
    if (error instanceof ApplicationsApiError && error.kind === 'http' && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/** Server-complete London viewport cells, independent of legacy snapshot paging. */
export async function fetchLondonPlanningMapAggregates(options: FetchPlanningFeedLightPageOptions & { bounds: PlanningFeedBounds; zoom: number }) {
  if (!isLondonScope(options.council) || !options.bounds || options.cursor) {
    throw new ApplicationsApiError('London map requires a council scope and viewport', 'validation', LONDON_API_ORIGIN, 400);
  }
  if (!usesSharedLondonDelivery(options.council)) rejectUnsupportedLondonFilters(options);
  else if (options.limit != null || options.offset != null || options.includeFacets != null ||
      options.includeTotal != null || options.includeShortLets) {
    throw new ApplicationsApiError('Shared London maps require complete viewport aggregation','validation',LONDON_API_ORIGIN,400);
  }
  return londonOperation(() => readLondonMap({
    council: options.council, query: options.query, start: options.validatedFrom, end: options.validatedEndExclusive,
    bounds: { west: options.bounds.min_lng, south: options.bounds.min_lat, east: options.bounds.max_lng, north: options.bounds.max_lat },
    zoom: options.zoom, fetchImpl: options.fetchImpl, signal: options.signal,
    ...(usesSharedLondonDelivery(options.council) ? { filters:sharedLondonFilters(options) } : {}),
  }));
}

export const applicationsApiService = {
  fetchApplications,
  fetchApplicationsLight,
  fetchAllApplications,
  fetchAllApplicationsLight,
  fetchFeedLightSnapshot,
  fetchShortTermLets,
  fetchShortTermLetsLight,
  fetchAllShortTermLets,
  fetchAllShortTermLetsLight,
  fetchPlanningFeedLightSnapshot,
  fetchPlanningMapFeedPage,
  fetchPlanningMapFeedSnapshot,
  fetchShortTermLetsLightSnapshot,
  fetchShortTermLetsMapFeedSnapshot,
  fetchAllUnifiedPlanningFeed,
  fetchAllUnifiedPlanningFeedLight,
  fetchAllUnifiedPlanningMapFeed,
  fetchUnifiedPlanningFeed,
  fetchUnifiedPlanningFeedLight,
  fetchUnifiedPlanningMapFeed,
  fetchApplicationById,
  fetchApplicationByIdViaDetailProxy,
  fetchLondonPlanningMapAggregates,
};
